import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { TextField, Typography, Button, Box, Paper, List, ListItem, CircularProgress, Container, Grid, Card, CardContent, CardActions } from "@mui/material";
import { Document, Page, pdfjs } from "react-pdf";
import { Link } from "react-router-dom";
import Fuse from 'fuse.js';
import fundoImagem from '../../assets/ImgFundo/fundo-abstratoA.jpg'; // Ajuste o caminho conforme necessário
import SearchIcon from '@mui/icons-material/Search';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DocumentoDoVerbete = ({ showLink = true }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { id } = useParams();
  const location = useLocation();
  const [verbete, setVerbete] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loadingPdf, setLoadingPdf] = useState(true);
  const [loading, setLoading] = useState(false);
  const [bottomLoading, setBottomLoading] = useState(false);
  const [pageTexts, setPageTexts] = useState([]);
  const [autoSearchApplied, setAutoSearchApplied] = useState(false);
  const memoizedPdfUrl = useMemo(() => pdfUrl, [pdfUrl]);
  const urlQuery = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search]);
  const urlPage = useMemo(() => {
    const raw = Number(new URLSearchParams(location.search).get("page"));
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  }, [location.search]);

  const normalizeText = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

  const normalizeForSearch = (value) =>
    normalizeText(value).replace(/[^\p{L}\p{N}\s]+/gu, " ").replace(/\s+/g, " ").trim();

  const STOP_WORDS = new Set([
    "a", "o", "e", "de", "do", "da", "dos", "das", "em", "no", "na", "nos", "nas", "um", "uma", "uns", "umas",
    "ao", "aos", "com", "sem", "por", "para", "que", "se", "sua", "seu", "suas", "seus", "os", "as",
    "del", "la", "las", "el", "los", "y", "en", "con", "sin", "una", "uno", "unos",
  ]);

  const extractRelevantTokens = (rawQuery) => {
    const normalized = normalizeForSearch(rawQuery);
    const words = normalized.split(" ").filter(Boolean);
    const filtered = words.filter((word) => word.length >= 4 && !STOP_WORDS.has(word));

    let tokens = filtered;
    if (tokens.length === 0 && words.length === 1 && words[0].length >= 3) {
      tokens = [words[0]];
    } else if (tokens.length === 0) {
      tokens = words.filter((word) => word.length >= 3);
    }

    return [...new Set(tokens)].sort((a, b) => b.length - a.length).slice(0, 8);
  };

  const onSearchTextChange = (e) => {
    setSearchText(e.target.value);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const extractTextFromPdf = async (pdfUrl) => {
    const loadingTask = pdfjs.getDocument(pdfUrl);
    const doc = await loadingTask.promise;
    const pageTextsArray = [];

    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
      const page = await doc.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(" ");
      pageTextsArray.push({ pageNumber, text: pageText });
    }

    setPageTexts(pageTextsArray);
    return pageTextsArray;
  };

  const buildJsonIndexCandidates = (docData) => {
    const candidates = [];
    const explicitIndex = docData?.linkjsondocindex;

    if (explicitIndex && typeof explicitIndex === "string") {
      candidates.push(explicitIndex);
    }

    if (docData?.linkpdfdoclink && typeof docData.linkpdfdoclink === "string") {
      const base = docData.linkpdfdoclink.replace(/\.pdf(\?.*)?$/i, "");
      candidates.push(`${base}_normalizado.json`);
    }

    return [...new Set(candidates.filter(Boolean))];
  };

  const tryLoadJsonIndex = async (docData) => {
    const candidates = buildJsonIndexCandidates(docData);
    for (const url of candidates) {
      try {
        const response = await fetch(url, { cache: "no-store" });
        if (!response.ok) {
          continue;
        }

        const payload = await response.json();
        if (!Array.isArray(payload?.pages)) {
          continue;
        }

        const mappedPages = payload.pages
          .map((item, idx) => ({
            pageNumber: Number(item?.page) || idx + 1,
            text: String(item?.text || ""),
          }))
          .filter((item) => item.text.trim().length > 0);

        if (mappedPages.length > 0) {
          setPageTexts(mappedPages);
          return true;
        }
      } catch (error) {
        console.error("Falha ao carregar índice JSON do documento:", url, error);
      }
    }
    return false;
  };

  const computeSearchResults = (rawSearchText, pages = pageTexts) => {
    if (!rawSearchText || pages.length === 0) return [];
    const query = normalizeForSearch(rawSearchText);
    if (!query) return [];

    const queryTokens = extractRelevantTokens(rawSearchText);

    const scorePage = (pageTextRaw) => {
      const pageText = normalizeForSearch(pageTextRaw);
      if (!pageText) return 0;
      if (pageText.includes(query)) return 10000;

      let tokenMatches = 0;
      queryTokens.forEach((token) => {
        if (pageText.includes(token)) tokenMatches += 1;
      });

      let longestPhrase = 0;
      for (let start = 0; start < queryTokens.length; start += 1) {
        for (let end = queryTokens.length; end > start; end -= 1) {
          const phrase = queryTokens.slice(start, end).join(" ");
          if (phrase.length < 6) continue;
          if (pageText.includes(phrase)) {
            longestPhrase = Math.max(longestPhrase, end - start);
            break;
          }
        }
      }

      return tokenMatches * 10 + longestPhrase * 40;
    };

    let scoredResults = pages
      .map((item) => ({
        page: item.pageNumber,
        text: item.text,
        score: scorePage(item.text),
      }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || a.page - b.page);

    let results = scoredResults.map(({ page, text }) => ({ page, text }));

    if (results.length === 0) {
      const fuseOptions = {
        keys: ['text'],
        includeScore: true,
        threshold: 0.35,
        ignoreLocation: true,
        minMatchCharLength: 3,
      };

      const fuse = new Fuse(pages, fuseOptions);
      results = fuse.search(rawSearchText).map(result => ({
        page: result.item.pageNumber,
        text: result.item.text,
      }));
    }
    return results;
  };

  const handleSearch = async () => {
    setBottomLoading(true);
    setLoading(true);

    if (!searchText || !pdfUrl || pageTexts.length === 0) {
      setSearchResults([]);
      setLoading(false);
      setBottomLoading(false);
      return;
    }

    const results = computeSearchResults(searchText);

    setSearchResults(results);
    setLoading(false);
    setBottomLoading(false);

    if (results.length > 0) {
      setPageNumber(results[0].page);
    }
  };

  const customTextRenderer = (textItem) => {
    if (!searchText) return textItem.str;

    const originalText = textItem.str || "";
    const normalizedQueryTerms = extractRelevantTokens(searchText);
    const normalizedExactQuery = normalizeForSearch(searchText);
    if (normalizedQueryTerms.length === 0) return originalText;

    const normalizedChars = [];
    const originalIndexMap = [];

    for (let i = 0; i < originalText.length; i += 1) {
      const normalizedChar = originalText[i]
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

      for (let j = 0; j < normalizedChar.length; j += 1) {
        normalizedChars.push(normalizedChar[j]);
        originalIndexMap.push(i);
      }
    }

    const normalizedText = normalizedChars.join("");
    const ranges = [];

    if (normalizedExactQuery.length >= 12 && normalizedExactQuery.split(" ").length >= 3) {
      let fromIndex = 0;
      while (fromIndex < normalizedText.length) {
        const matchIndex = normalizedText.indexOf(normalizedExactQuery, fromIndex);
        if (matchIndex === -1) break;
        const start = originalIndexMap[matchIndex];
        const end = originalIndexMap[matchIndex + normalizedExactQuery.length - 1] + 1;
        ranges.push([start, end]);
        fromIndex = matchIndex + normalizedExactQuery.length;
      }
    }

    normalizedQueryTerms.forEach((term) => {
      let fromIndex = 0;
      while (fromIndex < normalizedText.length) {
        const matchIndex = normalizedText.indexOf(term, fromIndex);
        if (matchIndex === -1) break;

        const start = originalIndexMap[matchIndex];
        const end = originalIndexMap[matchIndex + term.length - 1] + 1;
        ranges.push([start, end]);
        fromIndex = matchIndex + term.length;
      }
    });

    if (ranges.length === 0) return originalText;

    ranges.sort((a, b) => a[0] - b[0]);
    const mergedRanges = [];
    ranges.forEach(([start, end]) => {
      const last = mergedRanges[mergedRanges.length - 1];
      if (!last || start > last[1]) {
        mergedRanges.push([start, end]);
      } else {
        last[1] = Math.max(last[1], end);
      }
    });

    const escapeHtml = (value) =>
      String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    let html = "";
    let cursor = 0;

    mergedRanges.forEach(([start, end]) => {
      if (start > cursor) {
        html += escapeHtml(originalText.slice(cursor, start));
      }
      html += `<span class="pdf-search-hit">${escapeHtml(originalText.slice(start, end))}</span>`;
      cursor = end;
    });

    if (cursor < originalText.length) {
      html += escapeHtml(originalText.slice(cursor));
    }

    return html;
  };

  const handleResultClick = (page) => {
    setPageNumber(page);
  };

  useEffect(() => {
    const verbeteUrl = `${apiUrl}/verbetes/${id}`;

    async function fetchVerbete() {
      setLoadingPdf(true);

      try {
        const res = await fetch(verbeteUrl);
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        setVerbete(data);

        if (data?.linkpdfdoclink) {
          setPdfUrl(data.linkpdfdoclink);
          const hasJsonIndex = await tryLoadJsonIndex(data);
          if (!hasJsonIndex) {
            await extractTextFromPdf(data.linkpdfdoclink);
          }
        } else {
          console.error("Nenhum PDF encontrado no verbete.");
        }
      } catch (error) {
        console.error("Erro ao buscar verbete:", error);
      } finally {
        setLoadingPdf(false);
      }
    }

    fetchVerbete();
  }, [id, apiUrl]);

  useEffect(() => {
    setAutoSearchApplied(false);
  }, [id, location.search]);

  useEffect(() => {
    if (autoSearchApplied || pageTexts.length === 0) return;

    if (urlPage > 0) {
      setPageNumber(urlPage);
    }

    if (urlQuery.trim()) {
      setSearchText(urlQuery);
      const results = computeSearchResults(urlQuery, pageTexts);
      setSearchResults(results);

      if (results.length > 0) {
        const preferred = results.find((result) => result.page === urlPage);
        setPageNumber(preferred ? preferred.page : results[0].page);
      }
    }

    setAutoSearchApplied(true);
  }, [autoSearchApplied, pageTexts, urlPage, urlQuery]);

  return (

    <Box
      sx={{
        minHeight: '100vh',
        background: `url(${fundoImagem}) no-repeat center center fixed`, // Usando a imagem importada da pasta assets
        backgroundSize: 'cover',
        padding: '1rem',
        width: '100%', // Faz o Box ocupar 100% da largura disponível
        maxWidth: '1500px', // Aumenta a largura máxima
        margin: '0 auto', // Centraliza o Box na tela
      }}
    >
      <style>
        {`
          .react-pdf__Page__textContent .pdf-search-hit {
            background: rgba(255, 230, 0, 0.78);
            border-radius: 2px;
            box-shadow: inset 0 0 0 1px rgba(210, 170, 0, 0.45);
          }
        `}
      </style>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>

                <hr style={{ margin: '40px 0', borderTop: '1px solid #ddd' }} />
                <hr style={{ margin: '40px 0', borderTop: '1px solid #ddd' }} />

                {verbete && (
                  <>
                    <Typography
                      variant="h2"
                      align="center"
                      gutterBottom
                      style={{
                        fontFamily: "'Lato', serif",
                        color: "#800020",
                        textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
                        borderBottom: "4px solid #d4af37",
                        paddingBottom: "10px",
                        textTransform: "uppercase",
                        animation: "glow 2s infinite alternate",
                        marginTop: "7%", // Espaço superior
                      }}
                    >
                      {verbete.titulo}
                    </Typography>

                    {/* Container para centralizar o nome do autor */}
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ marginTop: 2 }} // Espaço entre o título e o autor
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          textAlign: "center",
                          color: "#444",
                          fontFamily: "'Lato', sans-serif",
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                          padding: "8px 0",
                          borderBottom: "2px solid rgba(0, 0, 0, 0.1)",
                          display: "inline-block",
                        }}
                      >
                        {verbete.autor}
                      </Typography>
                    </Box>

                    {/* Botão de "Visualizar Verbete" */}
                    {showLink && (
                      <Box
                        display="flex"
                        justifyContent="center"
                        sx={{ marginTop: 3 }} // Espaço entre o autor e o botão
                      >
                        <Link to={`/Verbetes/${verbete._id}`} style={{ textDecoration: 'none' }}>
                          <Button variant="contained" color="primary">
                            Visualizar Verbete
                          </Button>
                        </Link>
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    gap: 2, // Espaçamento entre o campo de busca e o botão
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Buscar no documento"
                    placeholder="Digite sua busca..."
                    value={searchText}
                    onChange={onSearchTextChange}
                    sx={{
                      borderRadius: 1, // Bordas arredondadas
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1, // Bordas arredondadas no input
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary', // Cor do label
                      },
                      '& .MuiOutlinedInput-input': {
                        color: 'text.primary', // Cor do texto digitado
                      },
                    }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{
                      borderRadius: 1, // Bordas arredondadas
                      textTransform: 'none', // Remove uppercase
                      fontWeight: 600, // Texto em negrito
                      px: 3, // Padding horizontal
                      py: 1.5, // Padding vertical
                      '&:hover': {
                        backgroundColor: 'primary.dark', // Cor ao passar o mouse
                      },
                      transition: 'background-color 0.2s ease-in-out', // Transição suave
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} sx={{ color: 'common.white' }} />
                    ) : (
                      <>
                        <SearchIcon sx={{ mr: 1 }} /> {/* Ícone de busca */}
                        Buscar
                      </>
                    )}
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2, // Espaçamento menor entre os elementos
                    mb: 2,
                  }}
                >
                  <Button
                    onClick={() => setPageNumber(pageNumber - 1)}
                    variant="outlined"
                    disabled={pageNumber <= 1}
                  >
                    Página anterior
                  </Button>

                  <Typography variant="body1">
                    Página {pageNumber} de {numPages}
                  </Typography>

                  <Button
                    onClick={() => setPageNumber(pageNumber + 1)}
                    variant="outlined"
                    disabled={pageNumber >= numPages}
                  >
                    Próxima página
                  </Button>
                </Box>

                <Grid container spacing={3}>
                  {/* Contêiner do PDF */}
                  <Grid item xs={8}>
                    <Paper
                      sx={{
                        p: 2,
                        maxWidth: '100%', // Limita a largura máxima
                        maxHeight: '80vh', // Limita a altura máxima
                        overflow: 'auto', // Adiciona scroll se o conteúdo for maior que o contêiner
                      }}
                    >
                      <Document
                        file={memoizedPdfUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={(error) => {
                          console.error("Erro ao carregar PDF:", error);
                          setLoadingPdf(false);
                        }}
                      >
                        <Page
                          pageNumber={pageNumber}
                          renderTextLayer
                          customTextRenderer={customTextRenderer}
                          width={800} // Define uma largura fixa para o PDF (ajuste conforme necessário)
                        />
                      </Document>
                    </Paper>
                  </Grid>

                  {/* Contêiner dos resultados */}
                  <Grid item xs={4}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 3,
                        backgroundColor: 'background.paper',
                        maxHeight: '80vh', // Limita a altura máxima
                        overflow: 'auto', // Adiciona scroll se o conteúdo for maior que o contêiner
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: searchResults.length > 0 ? 'blue' : 'red',
                          textTransform: 'uppercase',
                          borderBottom: '2px solid #d4af37',
                          pb: 1,
                        }}
                      >     Resultados encontrados
                      </Typography>

                      {searchResults.length > 0 ? (
                        <List>
                          {searchResults.map((result, index) => (
                            <ListItem
                              key={index}
                              button
                              onClick={() => handleResultClick(result.page)}
                              sx={{
                                borderRadius: 1,
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                                transition: 'background-color 0.2s ease-in-out',
                                mb: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: 'primary.main', // Azul para resultados encontrados
                                }}
                              >                <strong>Página {result.page}</strong>
                              </Typography>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'error.main', // Vermelho para nenhum resultado encontrado
                            fontStyle: 'italic',
                          }}
                        >
                          Nenhum resultado encontrado.
                        </Typography>
                      )}

                      {bottomLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <CircularProgress size={24} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>


  );
};

export default DocumentoDoVerbete;
